import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WeekSection from '../components/WeeklyNews/WeekSection';
import DaySection from '../components/DailyNews/DaySection';
import Loading from '../components/Common/Loading';
import { PageErrorBoundary } from '../components/Common/ErrorBoundary';
import { articleService } from '../services/articleService';
import { dailySummaryService } from '../services/dailySummaryService';
import { weeklySummaryService } from '../services/weeklySummaryService';
import { Article } from '../config/api';
import { DailySummaryData } from '../components/DailyNews/DailySummary';
import { WeeklySummaryData } from '../types/weekly';
import { getPSTWeekStart, getPSTWeekEnd, dbDateToPSTDate } from '../utils/timezone';
import './Home.css';

interface GroupedData {
  dailySummaries: DailySummaryData[];
  articles: Article[];
  weeklySummary: WeeklySummaryData | null;
  lastWeekSummaries?: DailySummaryData[];
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<GroupedData>({
    dailySummaries: [],
    articles: [],
    weeklySummary: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const summariesResponse = await dailySummaryService.getRecentDailySummaries(14);
        const allSummaries = summariesResponse || [];

        const startOfWeek = getPSTWeekStart();
        const endOfWeek = getPSTWeekEnd();

        const currentWeekSummaries = allSummaries.filter(summary => {
          const summaryPSTDate = dbDateToPSTDate(summary.date);
          return summaryPSTDate >= startOfWeek && summaryPSTDate <= endOfWeek;
        });

        // Fetch articles for current week summaries by their IDs
        const currentWeekSummaryIds = currentWeekSummaries.map(summary => summary.id);
        let currentWeekArticles: Article[] = [];
        
        if (currentWeekSummaryIds.length > 0) {
          const articlesResponse = await articleService.getArticles({
            dailySummaryIds: currentWeekSummaryIds,
            limit: 1000,
            status: 'active'
          });
          currentWeekArticles = articlesResponse.data || [];
        }

        // Get the latest weekly summary
        let weeklySummary: WeeklySummaryData | null = null;
        let lastWeekSummaries: DailySummaryData[] = [];
        
        try {
          const weeklyData = await weeklySummaryService.getWeeklySummaries({ limit: 1 });
          if (weeklyData.data?.length > 0) {
            weeklySummary = weeklyData.data[0];
            
            // Get daily summaries for this weekly summary
            try {
              lastWeekSummaries = await dailySummaryService.getDailySummariesByWeek(weeklySummary.id.toString());
            } catch (error) {
              console.error('Error fetching daily summaries for week:', error);
              lastWeekSummaries = [];
            }
          }
        } catch (error) {
          console.error('Error fetching weekly summary:', error);
        }

        setData({
          articles: currentWeekArticles,
          dailySummaries: currentWeekSummaries,
          weeklySummary,
          lastWeekSummaries
        });
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load content. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const groupArticlesByDailySummaryId = (articles: Article[]): { [summaryId: number]: Article[] } => {
    const grouped: { [summaryId: number]: Article[] } = {};
    articles.forEach(article => {
      if (article.daily_summary_id) {
        if (!grouped[article.daily_summary_id]) {
          grouped[article.daily_summary_id] = [];
        }
        grouped[article.daily_summary_id].push(article);
      }
    });
    return grouped;
  };

  const handleDayClick = (dailySummary: DailySummaryData) => {
    const daySlug = dailySummary.date?.split('T')[0] || dailySummary.date || dailySummary.id;
    navigate(`/daily/${daySlug}`);
  };

  const handleWeekClick = (weeklySummary: WeeklySummaryData) => {
    navigate(`/archives/week/${weeklySummary.id}`);
  };

  if (loading) {
    return (
      <div className="home-container">
        <Loading 
          type="skeleton-list" 
          size="large" 
          message="Loading latest AI business news..." 
          centered={true}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-container">
        <div className="error-state">
          <h3>Unable to Load Content</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { articles, dailySummaries, weeklySummary } = data;
  const groupedArticles = groupArticlesByDailySummaryId(articles);

  return (
    <PageErrorBoundary>
      <div className="home-container">
        <main className="home-content">
          <section className="home-section">
            <h2 className="section-title">This Week's Daily Updates</h2>
            
            {dailySummaries.length > 0 ? (
              <div className="daily-sections">
                {dailySummaries
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((dailySummary) => {
                    const dayArticles = groupedArticles[dailySummary.id] || [];
                    
                    return (
                      <DaySection
                        key={dailySummary.id}
                        dailySummary={dailySummary}
                        articles={dayArticles}
                        defaultExpanded={true}
                        showThemes={true}
                        showStats={true}
                        showArticleTags={true}
                      />
                    );
                  })}
              </div>
            ) : (
              <div className="no-content">
                <h3>No Recent Updates</h3>
                <p>Check back soon for the latest AI business developments.</p>
              </div>
            )}
          </section>

          {weeklySummary && (
            <section className="home-section">
              <h2 className="section-title">Last Week's Summary</h2>
              <WeekSection
                weeklySummary={weeklySummary}
                dailySummaries={data.lastWeekSummaries || []}
                defaultExpanded={true}
                showThemes={true}
                showStats={true}
                onDayClick={handleDayClick}
                onWeekClick={handleWeekClick}
              />
            </section>
          )}
        </main>
      </div>
    </PageErrorBoundary>
  );
};

export default Home;
const getISOWeekInfo = (date: Date) => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = target.getUTCDay() || 7;
  if (day !== 1) {
    target.setUTCDate(target.getUTCDate() + (1 - day));
  }
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((target.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return {
    weekNumber: week,
    year: target.getUTCFullYear(),
  };
};
